import React, { useEffect, useMemo, useState } from "react";
import { DailogBox } from "../../../components/Elements/DailogBox/DailogBox";
import CardsBorder from "../../../components/Cards/CardsBorder";
import SelectList from "../../../components/common/SelectList";
import CustomButton from "../../../components/Elements/Buttons/CustomButtons";
import { getApi, postApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";

const AddNewDynamicForm = ({
  open,
  onCancel,
  projectId,
  currentForms, // ✅ comes from parent DynamicForm
  clientId,
  onSave,
  saveFailed,
  setFailedMsg,
}) => {
  const [selectedForms, setSelectedForms] = useState([]);
  const [dynamicFormsList, setDynamicFormsList] = useState([]);
  const [popup, setPopup] = useState({
    open: false,
    type: "",
    title: "",
    message: "",
  });

  // ✅ Fetch forms
  const fetchForms = async () => {
    try {
      const response = await getApi({
        apiUrl: API_URLS.GET_ALL_CUSTOMFORMS,
        headers: { client: clientId },
      });

      if (response?.success) {
        // ✅ Extract keys from currentForms
        const assignedIds = currentForms?.map((f) => f.formId || f.key) || [];

        const formattedForms = response.data.data.map((formData) => ({
          label: formData.title,
          value: formData._id,
          formId: formData._id,
          disabled: assignedIds.includes(formData._id),
        }));

        setDynamicFormsList(formattedForms);

        // ✅ Auto-select already assigned forms
        setSelectedForms(assignedIds);
      }
    } catch (error) {
      setPopup({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to fetch forms.",
      });
      console.error("Error fetching standard survey form list", error);
    }
  };

  const handleSelectedFormChange = (value) => {
    // ✅ do nothing if that item is disabled (already assigned)
    const isDisabled = dynamicFormsList.find(
      (f) => f.value === value
    )?.disabled;
    if (isDisabled) return;

    setSelectedForms((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleCancel = () => {
    setSelectedForms([]);
    onCancel();
  };

  const saveForms = async (payload, projectId) => {
    try {
      const response = await postApi({
        apiUrl: API_URLS.ADDNEW_CUSTOM_FORM_BY_PROJECTID(projectId),
        data: payload,
      });
      if (response.success) {
        onSave();
        onCancel();
      } else {
        setFailedMsg(response.message);
        saveFailed();
      }
    } catch (error) {
      console.error("Error saving custom forms:", error);
    }
  };

  const handleSave = async () => {
    if (dynamicFormsList.length === 0) {
      setPopup({
        open: true,
        type: "error",
        title: "No Forms",
        message: "0 forms available for selection.",
      });
      return;
    }

    if (selectedForms.length === 0) {
      setPopup({
        open: true,
        type: "warning",
        title: "No Selection",
        message: "Please select a form first.",
      });
      return;
    }

    const selectedFormIds = dynamicFormsList
      .filter((form) => selectedForms.includes(form.value))
      .map((form) => form.formId);

    const payload = { custom_form: selectedFormIds };

    await saveForms(payload, projectId);
  };

  useEffect(() => {
    if (popup.open) {
      const timer = setTimeout(() => {
        setPopup((prev) => ({ ...prev, open: false }));
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [popup.open]);

  useEffect(() => {
    if (clientId && open) {
      fetchForms();
      console.log("Forms", currentForms);
    }
  }, [clientId, open]);

  return (
    <>
      <DailogBox open={open} onCancel={handleCancel} width={800}>
        <div className="p-4">
          <CardsBorder>
            <div className="py-6">
              <SelectList
                list={dynamicFormsList}
                title="Select Form"
                selecteditems={selectedForms}
                onChange={handleSelectedFormChange}
                selectionType="checkbox"
                listStyle="box"
                // ✅ Ensure SelectList respects disabled items
              />
            </div>
          </CardsBorder>
        </div>
        <div className="mb-4">
          <div className="flex justify-center gap-4 py-6">
            <CustomButton
              buttonType="primary"
              variant="cancel"
              buttonText="Cancel"
              className="text-base font-normal h-[50px] w-[200px]"
              onClick={handleCancel}
            />
            <CustomButton
              buttonType="primary"
              variant="ok"
              buttonText="Save"
              className="text-base font-normal h-[50px] w-[200px]"
              onClick={handleSave}
            />
          </div>
        </div>
      </DailogBox>

      {popup.open && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
          <PopupCard
            type={popup.type}
            title={popup.title}
            message={popup.message}
          />
        </div>
      )}
    </>
  );
};

export default AddNewDynamicForm;
